import React, { useEffect, useMemo } from 'react'
import { BpButton } from '../../../../shared/components/buttons/bp-button/BpButton'
import { Controller, useForm } from 'react-hook-form'
import { BpInput } from '../../../../shared/components/forms/input/BpInput'
import { BpSelect } from '../../../../shared/components/forms/select/BpSelect'
import { BpCheckbox } from '../../../../shared/components/forms/checkbox/BpCheckbox'
import { BpTextEditor } from '../../../../shared/components/forms/text-editor/TextEditor'
import {
  FilterData,
  PostComplianceSendAbuseMailApiArg,
  useGetComplianceGetEmailListQuery,
  useGetComplianceLastSendEmailQuery,
  useLazyGetSiteGoogleReportOauthUrlQuery,
  usePostComplianceSendAbuseMailMutation,
} from '../../../../app/api'
import { ObjectToArray } from '../../../../shared/lib/helpers/ObjectToArray'
import { IconUser } from '../../../../shared/components/icons/IconUser'
import { EMAIL_REGEX } from '../../../../shared/consts/RegularExpressions.const'
import { hideModal, showAlert } from '../../../../app/store/reducers/AppSlice'
import { useDispatch } from 'react-redux'
import { Preloader } from '../../../../shared/components/cards/preloader/Preloader'
import { useAppSelector } from '../../../../app/store/hooks/Redux'
import { IPpcComplianceFilter } from '../../model/types/PpcComplianceFilter.interface'
import { selectPpcComplianceFilter } from '../../model/ppcComplianceSlice'
import { selectUser } from '../../../../app/store/reducers/AuthSlice'
import { IUserModel } from '../../../../app/store/models/User.model'
import { useTranslation } from 'react-i18next'
import { IconPlus } from '../../../../shared/components/icons/IconPlus'
import { useLocation } from 'react-router-dom'
import { IconGmail } from '../../../../shared/components/icons/IconGmail'
import clsx from 'clsx'
import styles from './AdsReport.module.scss'

export interface PpcComplianceReportAdsProps {
  id: number
  adsCount: number
  affiliate: string
  projectId: number
  networkId: number
  networkName?: string | null
}

export const AdsReport = ({
  id,
  adsCount,
  affiliate,
  projectId,
  networkId,
  networkName,
}: PpcComplianceReportAdsProps) => {
  const { t } = useTranslation()
  const location = useLocation()
  const {
    country,
    dateRange,
    site,
    engine,
    browser,
    city,
    adHijackers,
    advertiserName,
    reversed_filters,
  } = useAppSelector<IPpcComplianceFilter>(selectPpcComplianceFilter)
  const { email, allow_sending_from_linked_emails } = useAppSelector<IUserModel>(selectUser)
  const {
    control,
    resetField,
    handleSubmit,
    reset,
    register,
    formState: { isValid, errors },
  } = useForm<PostComplianceSendAbuseMailApiArg['body']>({
    mode: 'onChange',
    defaultValues: {
      rememberForNetwork: !!networkName,
      rememberForAffiliate: true,
      markAffiliate: false,
      subject: t('fields.other.ceaseAndDesist'),
    },
  })
  const dispatch = useDispatch()

  const {
    data: lastSendEmail,
    isLoading,
    isFetching,
  } = useGetComplianceLastSendEmailQuery(
    {
      affiliateId: id,
      projectId: projectId,
      networkId: networkId,
    },
    {
      selectFromResult: (state) => {
        return { ...state, data: state.data?.data?.items?.[0] }
      },
    },
  )
  const [sendAbuseMail, { isLoading: sendAbuseMailIsLoading }] =
    usePostComplianceSendAbuseMailMutation()
  const [getGoogleReportOauthUrl] = useLazyGetSiteGoogleReportOauthUrlQuery()
  const { data: emailList } = useGetComplianceGetEmailListQuery()
  const emails = useMemo(
    () => emailList?.data?.map((value) => ({ text: value, value })),
    [emailList],
  )

  useEffect(() => {
    lastSendEmail &&
      reset({
        to: lastSendEmail.send_to.join(','),
        carbonCopy: ObjectToArray(lastSendEmail.carbonCopy).join(',') || email,
        subject: lastSendEmail.subject || t('fields.other.ceaseAndDesist'),
        body: lastSendEmail.body as string,
      })
  }, [lastSendEmail])

  if (isLoading || isFetching || !emails) {
    return (
      <div className={clsx(styles.preloaderWrapper)}>
        <Preloader />
      </div>
    )
  }

  return (
    <div className={clsx(styles.root)}>
      <h3 className={clsx(styles.header)}>
        <span className={clsx(styles.titleText)}>
          {t('pages.PPCCompliance.editEmailForAffiliate')} {affiliate}
        </span>
      </h3>
      <div className={clsx(styles.content)}>
        <form className={clsx(styles.form)}>
          <Controller
            render={({ field: { onChange, ...fieldProps } }) => (
              <BpSelect
                dropPosition={'auto'}
                {...fieldProps}
                required
                bottomSection={
                  allow_sending_from_linked_emails && (
                    <button
                      className={clsx(styles.gmailButton)}
                      type={'button'}
                      onClick={() =>
                        getGoogleReportOauthUrl()
                          .unwrap()
                          .then((response) => {
                            if (!response.data?.auth_url) return

                            localStorage.setItem('last-path', location.pathname + location.search)
                            window.location.href = response.data.auth_url
                          })
                          .catch(() => undefined)
                      }
                    >
                      <div className={clsx(styles.gmailButtonInner)}>
                        <div className={clsx(styles.gmailIcon)}>
                          <IconGmail />
                        </div>
                        {t('pages.PPCCompliance.buttons.connectGmail')}
                      </div>
                      <div className={clsx(styles.gmailPlus)}>
                        <IconPlus color={'#A059FF'} />
                      </div>
                    </button>
                  )
                }
                Icon={IconUser}
                defaultValue={emails && emails[0].value}
                fieldSize={'lg'}
                label={t('fields.from')}
                options={emails}
                {...register('from', {
                  required: 'Required',
                })}
              ></BpSelect>
            )}
            control={control}
            name={'from'}
          />
          <Controller
            render={({ field }) => (
              <BpInput
                {...field}
                required
                error={errors.to?.message}
                fieldSize={'lg'}
                label={t('fields.to')}
                placeholder={t('fields.receivers')}
                onClear={() => resetField('to', { defaultValue: '' })}
                {...register('to', {
                  required: 'Required',
                  validate: (value) => {
                    const emails = value?.split(/\s*,\s*/)

                    return value
                      ? emails.find((email) => !EMAIL_REGEX.test(email)) &&
                          t('notifications.invalidEmail')
                      : true
                  },
                })}
              />
            )}
            control={control}
            name='to'
          />
          {networkName && (
            <Controller
              render={({ field: { value, ...fieldProps } }) => (
                <BpCheckbox
                  {...fieldProps}
                  defaultChecked={value}
                  label={t('fields.rememberNetwork', { name: networkName })}
                />
              )}
              control={control}
              name='rememberForNetwork'
            />
          )}
          <Controller
            render={({ field: { value, ...fieldProps } }) => (
              <BpCheckbox
                {...fieldProps}
                defaultChecked={value}
                label={t('fields.rememberForThisAffiliate')}
              />
            )}
            control={control}
            name='rememberForAffiliate'
          />
          <Controller
            render={({ field }) => (
              <BpInput
                {...field}
                required
                error={errors.carbonCopy?.message}
                fieldSize={'lg'}
                label={t('fields.carbonCopy')}
                placeholder={t('fields.receivers')}
                onClear={() => resetField('carbonCopy', { defaultValue: '' })}
                {...register('carbonCopy', {
                  required: 'Required',
                  validate: (value) => {
                    const emails = value?.split(/\s*,\s*/) ?? []

                    return value
                      ? emails.find((email) => !EMAIL_REGEX.test(email)) &&
                          t('notifications.invalidEmail')
                      : true
                  },
                })}
              />
            )}
            control={control}
            name='carbonCopy'
          />
          <Controller
            render={({ field }) => (
              <BpInput
                {...field}
                error={errors.subject?.message}
                fieldSize={'lg'}
                label={t('fields.subject')}
                onClear={() => resetField('subject', { defaultValue: '' })}
                {...register('subject', {
                  maxLength: {
                    value: 512,
                    message: t('notifications.textLength'),
                  },
                })}
              />
            )}
            control={control}
            name='subject'
          />
          <div>
            <div className={clsx(styles.sectionTitle)}>{t('fields.attachments')}</div>
            <div className={clsx(styles.buttonsRow)}>
              <div className={'bp-btn sm pointer-events-none outline'}>
                {t('pages.PPCCompliance.latestAdReportPDF')}
              </div>
              <div className={'bp-btn sm pointer-events-none outline'}>
                {t('pages.PPCCompliance.reportCountAdsXLS', { count: adsCount })}
              </div>
            </div>
          </div>
          <Controller
            render={({ field }) => (
              <BpTextEditor
                {...field}
                label={t('fields.messageText')}
                max={4096}
                placeholder={t('fields.messageTextExample')}
              />
            )}
            control={control}
            name='body'
          />
          <Controller
            render={({ field: { value, ...fieldProps } }) => (
              <BpCheckbox
                {...fieldProps}
                defaultChecked={value}
                label={t('fields.markAffiliateAsInProgress')}
              />
            )}
            control={control}
            name='markAffiliate'
          />
        </form>
      </div>
      <div className={clsx(styles.footer)}>
        <BpButton
          className={clsx(styles.button)}
          disabled={!isValid}
          loading={sendAbuseMailIsLoading}
          onClick={handleSubmit((data) =>
            sendAbuseMail({
              body: {
                ...data,
                filterData: {
                  date: dateRange,
                  project_id: projectId,
                  affiliate_id: id,
                  affiliate: affiliate,
                  network_id: networkId,
                  country_id: country || undefined,
                  site: site,
                  browser_id: browser || undefined,
                  engine_id: engine || undefined,
                  city_id: city || undefined,
                  advertiser_id: advertiserName || undefined,
                  // filterData уходит JSON-ом, и по схеме здесь массив, а не строка через запятую
                  reversed_filters: reversed_filters ? reversed_filters.split(',') : undefined,
                  is_hijack: adHijackers,
                } as unknown as FilterData,
              },
            }).then((response) => {
              if ('error' in response || !response.data.success) {
                return
              } else {
                dispatch(hideModal())
                dispatch(
                  showAlert({
                    message: t('notifications.emailSent'),
                    type: 'success',
                  }),
                )
              }
            }),
          )}
        >
          {t('buttons.sendMail')}
        </BpButton>
      </div>
    </div>
  )
}
